import { Search } from "lucide-react";
import { type FC, useEffect } from "react";

import type { OptionItem } from "@/api/admin";
import {
  CustomInput,
  DropdownMenuCheckboxItemNoIndicator,
  DropdownMenuRadioItemNoIndicator,
} from "@/components/atoms";
import {
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { formatTestId } from "@/utils/formatTestId";

interface CategorySearchSubMenuProps {
  items: OptionItem[];
  selectedItems: OptionItem[];
  toggleItem: (item: OptionItem) => void;
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  placehoder: string;
  isSingleSelect?: boolean;
}

const CategorySearchSubMenu: FC<CategorySearchSubMenuProps> = ({
  items,
  selectedItems,
  toggleItem,
  searchTerm,
  setSearchTerm,
  placehoder,
  isSingleSelect = false,
}) => {
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isChecked = (item: OptionItem) =>
    selectedItems.some(i => i.id === item.id);

  const isEmpty = filteredItems.length === 0;

  useEffect(() => {
    setSearchTerm("");
  }, [setSearchTerm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Escape") {
      e.stopPropagation();
    }
  };

  const ItemComponent = isSingleSelect
    ? DropdownMenuRadioItemNoIndicator
    : DropdownMenuCheckboxItemNoIndicator;

  return (
    <>
      <DropdownMenuLabel
        className="flex items-center gap-2 relative"
        data-testid="category-search-label"
      >
        <Search
          size={16}
          className="absolute top-2 left-2 z-20"
          color="#6B7280"
        />
        <CustomInput
          type="text"
          placeholder={placehoder}
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          className="placeholder:text-xs text-gray-950 font-normal border-none h-5 pl-6 focus-visible:outline-none focus-visible:ring-0"
          data-testid="category-search-input"
          autoFocus
        />
      </DropdownMenuLabel>
      <DropdownMenuSeparator />

      <div className="relative min-h-[40px]">
        {isEmpty && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground pointer-events-none">
            Not found
          </div>
        )}

        {filteredItems.map(elem => (
          <ItemComponent
            key={elem.id}
            checked={isChecked(elem)}
            onCheckedChange={() => toggleItem(elem)}
            onPointerMove={e => e.preventDefault()}
            data-testid={`category-search-item-${formatTestId(elem.name)}`}
          >
            <span className="text-sm">{elem.name}</span>
          </ItemComponent>
        ))}
      </div>
    </>
  );
};

export default CategorySearchSubMenu;
